import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Receipt, AlertTriangle } from 'lucide-react';
import { netFromGross, taxFromNet, type TaxConfig } from '@/utils/tax';
import { formatMoney, resolveCompanyLocale } from '@/utils/locale';
import type { UseFormReturn } from 'react-hook-form';

interface TaxSettingsCardProps {
  // Untyped because the parent form has many other fields; we only read/write
  // the four tax_* keys here.
  form: UseFormReturn<any>;
  country?: string;
}

const COMMON_TAX_LABELS = ['GST', 'VAT', 'Sales Tax', 'HST', 'TVA', 'IVA'];

export function TaxSettingsCard({ form, country }: TaxSettingsCardProps) {
  const enabled = form.watch('tax_enabled');
  const rate = form.watch('tax_rate') ?? 0;
  const label = form.watch('tax_label') ?? 'Tax';
  const inclusive = form.watch('tax_inclusive');

  const cfg: TaxConfig = { enabled, rate, label, inclusive };
  const locale = resolveCompanyLocale(country);
  const isAU = locale.countryCode === 'AU';
  // Preview figures in the tenant's own currency.
  const formatCurrency = (n: number) => formatMoney(n, locale);

  // Live preview using $1,000 as the reference figure.
  const previewCustomerTotal = 1000;
  const previewNet = netFromGross(previewCustomerTotal, cfg);
  const previewTax = previewCustomerTotal - previewNet;
  const previewExclusiveGross = previewCustomerTotal + taxFromNet(previewCustomerTotal, cfg);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          <CardTitle>Tax Settings</CardTitle>
        </div>
        <CardDescription>
          Configure how tax (GST, VAT, Sales Tax, etc.) is applied to invoices and estimates.
          Changes only affect new records — existing jobs keep the rate they were created with.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={form.control}
          name="tax_enabled"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5 pr-4">
                <FormLabel className="text-base font-medium">
                  Apply tax to invoices and estimates
                </FormLabel>
                <FormDescription>
                  Turn off if your business does not collect sales tax, GST, or VAT.
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />

        {!enabled && isAU && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Australian businesses registered for GST are legally required to display GST on
              invoices. Confirm with your accountant before turning this off.
            </AlertDescription>
          </Alert>
        )}

        {enabled && (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="tax_label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax name</FormLabel>
                    <FormControl>
                      <Input
                        list="tax-label-suggestions"
                        placeholder="GST"
                        maxLength={20}
                        {...field}
                      />
                    </FormControl>
                    <datalist id="tax-label-suggestions">
                      {COMMON_TAX_LABELS.map((opt) => (
                        <option key={opt} value={opt} />
                      ))}
                    </datalist>
                    <FormDescription>
                      How tax is labelled on invoices, estimates, and reports.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tax_rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax rate (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="10"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Examples: 10 (AU GST), 20 (UK VAT), 8.25 (US sales tax).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tax_inclusive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax mode</FormLabel>
                  <Select
                    value={field.value ? 'inclusive' : 'exclusive'}
                    onValueChange={(v) => field.onChange(v === 'inclusive')}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="inclusive">Tax included in prices</SelectItem>
                      <SelectItem value="exclusive">Tax added on top of prices</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Inclusive matches the AU/UK norm; exclusive matches the US norm.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Live preview */}
            <div className="rounded-lg border bg-muted/40 p-4">
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                Preview
              </div>
              {inclusive ? (
                <p className="text-sm">
                  Customer total <span className="font-mono">{formatCurrency(previewCustomerTotal)}</span>{' '}
                  → Net <span className="font-mono">{formatCurrency(previewNet)}</span>{' '}
                  + {label}{' '}
                  <span className="font-mono">{formatCurrency(previewTax)}</span>
                </p>
              ) : (
                <p className="text-sm">
                  Net <span className="font-mono">{formatCurrency(previewCustomerTotal)}</span>{' '}
                  + {label}{' '}
                  <span className="font-mono">
                    {formatCurrency(taxFromNet(previewCustomerTotal, cfg))}
                  </span>{' '}
                  → Customer total{' '}
                  <span className="font-mono">{formatCurrency(previewExclusiveGross)}</span>
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
